import { useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import SecureInput from '../../../ui/SecureInput/SecureInput'

type Styles = { [key: string]: string }

type PreviewSectionProps = {
    styles: Styles
    authType: string
    testConnection: () => Promise<void> | void
    testing: boolean
    config: any
    testResult?: any
    form: Pick<UseFormReturn<any>, 'watch' | 'setValue'>
}

export default function PreviewSection(props: PreviewSectionProps) {
    const { styles, authType, testConnection, testing, config, testResult, form } = props;
    const [activeTab, setActiveTab] = useState<'headers' | 'summary' | 'test'>('headers')
   
    const { watch, setValue } = form

    const connectionSummary: string = (() => {
        const lines: string[] = []
        lines.push(`Base URL: ${watch('sap_base_url') || ''}`)
        switch (authType) {
            case 'basic':
                lines.push('Auth: Basic (dev/legacy)')
                break
            case 'oauth2_client_credentials':
                lines.push('Auth: OAuth2 Client Credentials (technical user)')
                lines.push(`Token URL: ${watch('oauth.token_url') || ''}`)
                lines.push(`Scope: ${watch('oauth.scope') || '<default>'}`)
                break
            case 'oauth2_obo_jwt':
                lines.push('Auth: OAuth2 On-Behalf-Of (JWT Bearer)')
                lines.push(`Token URL: ${watch('obo.token_url') || ''}`)
                lines.push(`Audience: ${watch('obo.audience') || ''}`)
                break
            case 'saml2_bearer':
                lines.push('Auth: SAML2 Bearer Assertion')
                lines.push(`Token URL: ${watch('saml.token_url') || ''}`)
                lines.push(`Client ID: ${watch('saml.client_id') || '<none>'}`)
                lines.push(`Scope: ${watch('saml.scope') || '<default>'}`)
                break
            case 'btp_destination':
                lines.push('Auth: SAP BTP Destination')
                lines.push(`Destination: ${watch('destination.name') || ''}`)
                lines.push(`Mode: ${watch('destination.auth') || ''}${watch('destination.forward_auth_token') ? ' + ForwardAuthToken' : ''}`)
                break
        }
        if (watch('advanced.via_cloud_connector')) lines.push(`Cloud Connector Location ID: ${watch('advanced.location_id') || '<default>'}`)
        if (watch('advanced.proxy_url')) lines.push(`Proxy: ${watch('advanced.proxy_url')}`)
        return lines.join('\n')
    })()

      const headersPreview: Record<string, string> = (() => {
        const h: Record<string, string> = {}
        if (authType === 'basic') h['Authorization'] = 'Basic <base64(username:password)>'
        if (authType === 'oauth2_client_credentials') h['Authorization'] = 'Bearer <access_token>'
        if (authType === 'oauth2_obo_jwt') h['Authorization'] = 'Bearer <user_delegated_access_token>'
        if (authType === 'saml2_bearer') h['Authorization'] = 'Bearer <saml_bearer_access_token>'
        if (watch('csrfEnabled')) h['x-csrf-token'] = 'Fetch (auto)'
        return h
      })()

    return (
        <div className={styles.preview}>
            <div className={styles.tabBar}>
                <button
                    type="button"
                    className={`${styles.tabBtn} ${activeTab === 'headers' ? styles.tabActive : ''}`}
                    onClick={() => setActiveTab('headers')}
                >
                    Request Headers
                </button>
                <button
                    type="button"
                    className={`${styles.tabBtn} ${activeTab === 'summary' ? styles.tabActive : ''}`}
                    onClick={() => setActiveTab('summary')}
                >
                    Connection Summary
                </button>
                <button
                    type="button"
                    className={`${styles.tabBtn} ${activeTab === 'test' ? styles.tabActive : ''}`}
                    onClick={() => setActiveTab('test')}
                >
                    Test
                </button>
            </div>

            {activeTab === 'headers' && (
                <div className={styles.section}>
                    <label className={styles.label}>Computed Headers</label>
                    {Object.keys(headersPreview).length === 0 ? (
                        <div className={styles.helpText}>No special headers yet.</div>
                    ) : (
                        <div className={styles.previewKVGrid}>
                            {Object.entries(headersPreview).map(([k, v]) => (
                                <>
                                    <div className={styles.previewKVKey}>{k}</div>
                                    <div className={styles.previewKVValue}>{v}</div>
                                </>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'summary' && (
                <div className={styles.section}>
                    <label className={styles.label}>Connection Summary</label>
                    <pre className={styles.previewSummaryBox}>{connectionSummary}</pre>
                </div>
            )}

            {activeTab === 'test' && (
                <div className={styles.section}>
                    <SecureInput
                        fieldType="text"
                        label="Relative Path"
                        value={watch('advanced.test_path') || ''}
                        onChange={(value) => setValue('advanced.test_path', value)}
                        className={styles.input}
                    />
                    <div className={styles.headerRowPreview}>
                        <button
                            type="button"
                            onClick={testConnection}
                            disabled={testing || !config}
                            className={styles.testBtn}
                        >
                            {testing ? 'Testing...' : 'Run Test'}
                        </button>
                        <span className={styles.helpText}>Example expects API_BUSINESS_PARTNER to be available.</span>
                    </div>
                    {testResult && (
                        <div className={styles.testResult}>
                            <div className={`${styles.resultCard} ${testResult.success ? styles.success : styles.error}`}>
                                <div className={styles.resultStatus}>
                                    {testResult.success ? '✅ Success' : '❌ Failed'}
                                </div>
                                <div className={styles.resultMessage}>{testResult.message}</div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
